/**
 * TanStack Query hooks over the accounting-api bank-feed routes (/v1/expense/bank/*).
 * The caller's Finicity customer + Connect URL are created server-side from the token —
 * the client sends nothing but its auth. Entity + owner isolation is enforced server-side.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { expenseFetch } from '@/expense/lib/expense-api';

/**
 * Narrowed browser view of a linked account (matches the server's ConnectionView). The aggregator
 * customer/account handles are deliberately NOT exposed to the SPA — the server owns them.
 */
export interface BankConnection {
  connectionId: number;
  institutionName: string | null;
  accountMask: string | null;
  status: 'ACTIVE' | 'REVOKED' | 'ERROR';
  lastSyncedTxnDate: string | null;
  lastSyncedAt: string | null;
  lastError: string | null;
}

export interface ImportResult {
  imported: number;
  skippedDuplicate: number;
  matched: number;
}

const keys = { connections: () => ['expense', 'bank', 'connections'] as const };

export function useBankConnections() {
  return useQuery({
    queryKey: keys.connections(),
    queryFn: () =>
      expenseFetch<BankConnection[]>('/v1/expense/bank/connections'),
  });
}

/** Generate a Finicity Connect URL for the caller (customer created-or-reused server-side). */
export function useConnectUrl() {
  return useMutation({
    mutationFn: () =>
      expenseFetch<{ url: string }>('/v1/expense/bank/connect-url', {
        method: 'POST',
      }),
  });
}

/**
 * Record the caller's linked accounts AFTER Finicity Connect completes — server-authoritative and
 * idempotent (the server resolves the customer from the token, lists accounts, reconciles). The SPA
 * calls this on the Connect 'done' signal / popup close; it sends no body. Returns the resulting
 * connections so the caller can tell whether aggregation has produced accounts yet (it can lag the
 * 'done' event — poll on a bounded window).
 */
export function useSyncConnections() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      expenseFetch<BankConnection[]>('/v1/expense/bank/connections/sync', {
        method: 'POST',
      }),
    onSuccess: (rows) => qc.setQueryData(keys.connections(), rows),
  });
}

/** Pull new card transactions for the caller's linked accounts and auto-match receipts. */
export function useBankImport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      expenseFetch<ImportResult>('/v1/expense/bank/import', { method: 'POST' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.connections() });
      qc.invalidateQueries({ queryKey: ['expense', 'reports'] });
    },
  });
}
